/**
 * WIP Revenue Report — /finance/reports/wip-revenue
 * G18: Show in-progress jobs with expected vs invoiced revenue, WIP accrual gap.
 * Calls GET /finance/reports/wip-revenue?from=&to=&status=all
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Activity, RefreshCw, AlertCircle } from 'lucide-react';

// ── Types ─────────────────────────────────────────────────────────────────────

type RecognitionBasis = 'PERCENTAGE_COMPLETION' | 'DELIVERY_COMPLETE' | 'MILESTONE';

interface WipJob {
  jobId: string;
  jobNumber: string;
  customer: string;
  jobStatus: string;
  completionPct: number;
  expectedRevenue: number;
  invoicedRevenue: number;
  accruedRevenue: number;
  uninvoicedRevenue: number;
  wipGap: number;
  recognitionBasis: RecognitionBasis;
  createdAt: string;
}

interface WipRevenueData {
  period: { from: string; to: string };
  jobs: WipJob[];
  totalExpected: number;
  totalInvoiced: number;
  totalAccrued: number;
  totalWipGap: number;
  totalUninvoiced: number;
}

function fmt(n: number) {
  return new Intl.NumberFormat('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
}

function thisYear() {
  const now = new Date();
  return { from: `${now.getFullYear()}-01-01`, to: now.toISOString().split('T')[0]! };
}

const STATUS_BADGE: Record<string, string> = {
  in_progress: 'bg-blue-50 text-blue-700 border-blue-200',
  pending:     'bg-yellow-50 text-yellow-700 border-yellow-200',
  on_hold:     'bg-gray-50 text-gray-600 border-gray-200',
};

const BASIS_LABEL: Record<RecognitionBasis, string> = {
  PERCENTAGE_COMPLETION: '% Complete',
  DELIVERY_COMPLETE:     'On Delivery',
  MILESTONE:             'Milestone',
};

// ── Component ─────────────────────────────────────────────────────────────────

export default function WipRevenuePage() {
  const y = thisYear();
  const [from, setFrom]   = useState(y.from);
  const [to, setTo]       = useState(y.to);
  const [status, setStatus] = useState('all');
  const [data, setData]   = useState<WipRevenueData | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const loadedRef = useRef(false);

  const fetchData = useCallback(async (f: string, t: string, s: string) => {
    setLoading(true);
    try {
      const res = await api.get<WipRevenueData>(`/finance/reports/wip-revenue?from=${f}&to=${t}&status=${s}`);
      setData(res ?? null);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(() => {
    loadedRef.current = false;
    fetchData(from, to, status);
  }, [fetchData, from, to, status]);

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    fetchData(from, to, status);
  }, [fetchData, from, to, status]);

  const filtered = data?.jobs.filter(j =>
    !search || j.jobNumber.toLowerCase().includes(search.toLowerCase()) ||
    j.customer.toLowerCase().includes(search.toLowerCase())
  ) ?? [];

  return (
    <div className="p-4 md:p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Activity className="h-6 w-6 text-blue-600" />
          <div>
            <h1 className="text-xl font-semibold">WIP Revenue</h1>
            <p className="text-sm text-muted-foreground">In-progress jobs — expected vs invoiced vs accrued</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <input type="date" value={from} onChange={e => { setFrom(e.target.value); loadedRef.current = false; }}
            className="border rounded px-2 py-1 text-sm" />
          <input type="date" value={to} onChange={e => { setTo(e.target.value); loadedRef.current = false; }}
            className="border rounded px-2 py-1 text-sm" />
          <select value={status} onChange={e => { setStatus(e.target.value); loadedRef.current = false; }}
            className="border rounded px-2 py-1 text-sm">
            <option value="all">All Status</option>
            <option value="in_progress">In Progress</option>
            <option value="pending">Pending</option>
            <option value="on_hold">On Hold</option>
          </select>
          <Button variant="outline" size="sm" onClick={refresh} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* KPI strip */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: 'Expected Revenue', value: data.totalExpected,  color: 'text-foreground' },
            { label: 'Invoiced',         value: data.totalInvoiced,   color: 'text-green-700' },
            { label: 'Accrued',          value: data.totalAccrued,    color: 'text-blue-700' },
            { label: 'Uninvoiced',       value: data.totalUninvoiced, color: 'text-orange-700' },
            { label: 'WIP Gap',          value: data.totalWipGap,     color: data.totalWipGap > 0 ? 'text-red-700' : 'text-green-700' },
          ].map(kpi => (
            <Card key={kpi.label}>
              <CardContent className="pt-4 pb-3">
                <p className="text-xs text-muted-foreground">{kpi.label}</p>
                <p className={`text-lg font-semibold font-mono ${kpi.color}`}>{fmt(kpi.value)}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Search */}
      <div className="flex items-center gap-2">
        <input
          type="text" placeholder="Search job# or customer…" value={search}
          onChange={e => setSearch(e.target.value)}
          className="border rounded px-3 py-1.5 text-sm w-64"
        />
        {search && (
          <span className="text-xs text-muted-foreground">{filtered.length} / {data?.jobs.length ?? 0} jobs</span>
        )}
      </div>

      {/* Table */}
      {data && (
        <Card>
          <CardContent className="pt-3 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30">
                  <TableHead className="font-medium whitespace-nowrap">Job #</TableHead>
                  <TableHead className="font-medium whitespace-nowrap">Customer</TableHead>
                  <TableHead className="font-medium whitespace-nowrap text-center">Status</TableHead>
                  <TableHead className="font-medium whitespace-nowrap text-center">Complete%</TableHead>
                  <TableHead className="font-medium whitespace-nowrap text-right">Expected</TableHead>
                  <TableHead className="font-medium whitespace-nowrap text-right">Invoiced</TableHead>
                  <TableHead className="font-medium whitespace-nowrap text-right">Accrued</TableHead>
                  <TableHead className="font-medium whitespace-nowrap text-right">Uninvoiced</TableHead>
                  <TableHead className="font-medium whitespace-nowrap text-right">WIP Gap</TableHead>
                  <TableHead className="font-medium whitespace-nowrap text-center">Basis</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="py-12 text-center text-muted-foreground">
                      {loading ? 'Loading…' : 'No WIP jobs found for this period'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map(j => (
                    <TableRow key={j.jobId} className="hover:bg-muted/10 cursor-pointer" onClick={() => window.open(`/finance/ledger?job=${j.jobNumber}`, '_blank')}>
                      <TableCell className="font-medium text-blue-700 whitespace-nowrap">{j.jobNumber}</TableCell>
                      <TableCell className="whitespace-nowrap">{j.customer}</TableCell>
                      <TableCell className="text-center">
                        <span className={`text-xs px-2 py-0.5 rounded border ${STATUS_BADGE[j.jobStatus] ?? 'bg-muted text-muted-foreground border-border'}`}>
                          {j.jobStatus.replace('_', ' ')}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-16 bg-gray-100 rounded-full h-1.5">
                            <div
                              className="bg-blue-500 h-1.5 rounded-full"
                              style={{ width: `${Math.min(100, j.completionPct)}%` }}
                            />
                          </div>
                          <span className="text-xs font-mono">{j.completionPct}%</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono whitespace-nowrap">{fmt(j.expectedRevenue)}</TableCell>
                      <TableCell className="text-right font-mono text-green-700 whitespace-nowrap">{fmt(j.invoicedRevenue)}</TableCell>
                      <TableCell className="text-right font-mono text-blue-700 whitespace-nowrap">{fmt(j.accruedRevenue)}</TableCell>
                      <TableCell className="text-right font-mono text-orange-700 whitespace-nowrap">{fmt(j.uninvoicedRevenue)}</TableCell>
                      <TableCell className={`text-right font-mono font-semibold whitespace-nowrap ${j.wipGap > 0 ? 'text-red-700' : 'text-green-700'}`}>
                        {fmt(j.wipGap)}
                      </TableCell>
                      <TableCell className="text-center whitespace-nowrap">
                        <span className="text-xs text-muted-foreground">{BASIS_LABEL[j.recognitionBasis]}</span>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
              {data && filtered.length > 0 && (
                <TableBody>
                  <TableRow className="bg-muted/40 font-semibold border-t-2">
                    <TableCell colSpan={4}>Total ({filtered.length} jobs)</TableCell>
                    <TableCell className="text-right font-mono">{fmt(filtered.reduce((s, j) => s + j.expectedRevenue, 0))}</TableCell>
                    <TableCell className="text-right font-mono text-green-700">{fmt(filtered.reduce((s, j) => s + j.invoicedRevenue, 0))}</TableCell>
                    <TableCell className="text-right font-mono text-blue-700">{fmt(filtered.reduce((s, j) => s + j.accruedRevenue, 0))}</TableCell>
                    <TableCell className="text-right font-mono text-orange-700">{fmt(filtered.reduce((s, j) => s + j.uninvoicedRevenue, 0))}</TableCell>
                    <TableCell className="text-right font-mono font-bold text-red-700">{fmt(filtered.reduce((s, j) => s + j.wipGap, 0))}</TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Empty state */}
      {!data && !loading && (
        <Card>
          <CardContent className="py-16 text-center text-muted-foreground">
            <AlertCircle className="h-12 w-12 mx-auto mb-3 opacity-20" />
            <p className="font-medium">No data loaded</p>
            <p className="text-sm mt-1">Adjust the date range and click Refresh</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
